import React, { useEffect } from 'react';
import useAxios from 'axios-hooks';
import PropTypes from 'prop-types';
import isString from 'lodash.isstring';
import isEmpty from 'lodash.isempty';
import cn from 'classnames';
import uniqBy from 'lodash.uniqby';
import uniqueId from 'lodash.uniqueid';

import { short } from '../../common/formatting/date';
import { Tab } from '../../../../components';
import isNull from 'lodash.isnull';

const getAnswer = (question, responses) => {
	if (
		responses &&
		responses.length === 1 &&
		isString(responses[0].care_quest_answer_text)
	) {
		if (question.question_type === 'Date') {
			return <p>{short(responses[0].care_quest_answer_text)}</p>;
		}
		if (isEmpty(responses[0].care_quest_answer_text.trim())) {
			return (
				<p>
					<em>NO RESPONSE</em>
				</p>
			);
		} else {
			return <p>{responses[0].care_quest_answer_text}</p>;
		}
	} else if (responses && responses.length > 0) {
		return responses.map(({ care_quest_answer }) =>
			isEmpty(care_quest_answer) ? (
				<p key={uniqueId('no-ans-')}>
					<em>NO RESPONSE</em>
				</p>
			) : (
				<p
					key={`${care_quest_answer.id}`}
					className={cn({
						'negative-sentiment': care_quest_answer.sentiment === 'Negative',
					})}
				>
					{care_quest_answer.answer_text}
				</p>
			)
		);
	}
};

const QuestionnaireTab = ({
	care_quest_response_groupId,
	completed,
	isActive,
	providerId,
}) => {
	const [
		{ data: questionnaireResponse /* loading: loadingOptions, error */ },
		loadCareQuestGroup,
	] = useAxios(
		{
			method: 'GET',
			params: {
				filter: JSON.stringify({
					include: [
						{
							relation: 'care_quest_responses',
							scope: {
								include: ['care_quest_question', 'care_quest_answer'],
							},
						},
						'completed_by_provider',
					],
				}),
			},
		},
		{ manual: true, autoCancel: false }
	);
	const [
		{ data: responseScore /* loading: loadingOptions, error */ },
		loadResponseScore,
	] = useAxios(
		{
			method: 'GET',
			url: `/providers/${providerId}/care-quest-response-group-score`,
		},
		{ manual: true, autoCancel: false }
	);

	useEffect(() => {
		if (completed) {
			loadCareQuestGroup({
				url: `/care_quest_response_groups/${care_quest_response_groupId}`,
			});
			if (!isNull(care_quest_response_groupId)) {
				loadResponseScore({
					params: { careQuestResponseGroupId: care_quest_response_groupId },
				});
			}
		}
	}, [care_quest_response_groupId, completed]);

	let questions;
	if (
		questionnaireResponse &&
		Array.isArray(questionnaireResponse.care_quest_responses)
	) {
		// convert structure
		questions = uniqBy(
			questionnaireResponse.care_quest_responses.map(
				({ care_quest_question }) => care_quest_question
			),
			'id'
		);
	}

	return (
		<Tab isActive={isActive} id={uniqueId('qrtab-')}>
			{completed ? (
				<div>
					{questionnaireResponse && questionnaireResponse.response_dttm && (
						<h4>
							Completed{' '}
							{short(questionnaireResponse.response_dttm, false, 'PP p')}{' '}
							{!isNull(responseScore) &&
								responseScore > 0 &&
								` / Total Score: ${responseScore}`}
						</h4>
					)}
					<ol>
						{questions &&
							questions.map((question) =>
								question.conditional_parent_question_id ? (
									<ul key={question.id}>
										<li>
											<strong>{question.question_text}</strong>
											<br />
											{getAnswer(
												question,
												questionnaireResponse.care_quest_responses.filter(
													({ care_quest_questionId }) =>
														care_quest_questionId === question.id
												)
											)}
										</li>
									</ul>
								) : (
									<li key={question.id}>
										<strong>{question.question_text}</strong>
										<br />
										{getAnswer(
											question,
											questionnaireResponse.care_quest_responses.filter(
												({ care_quest_questionId }) =>
													care_quest_questionId === question.id
											)
										)}
									</li>
								)
							)}
					</ol>
					{questionnaireResponse?.completed_by_provider && (
						<div>
							<strong>Completed by:</strong>
							<br />
							{questionnaireResponse.completed_by_provider.name_first}{' '}
							{questionnaireResponse.completed_by_provider.name_last}
						</div>
					)}
				</div>
			) : (
				<div>Patient has not started or completed this questionnaire.</div>
			)}
		</Tab>
	);
};

QuestionnaireTab.propTypes = {
	providerId: PropTypes.number,
	care_quest_response_groupId: PropTypes.number,
	completed: PropTypes.number,
	isActive: PropTypes.bool,
};

export default QuestionnaireTab;
